import { Label } from '@/components/ui/label';
import { NutrientRequirement } from '@/types/client';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

// Helper function to format source name for display
const formatSourceName = (source: string | null | undefined): string => {
  if (!source) return '';
  return source
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Helper function to get DRV fields from a nutrient
type DRVField = {
  key: string;
  label: string;
  value: any;
  unitOverride?: string;
  valueFormatter?: (value: any) => string;
  source?: string | null;
};

// Helper function to get source for a specific field
const getFieldSource = (nutrient: any, fieldKey: string): string | null => {
  // Check fieldSources first (specific source for this field)
  if (nutrient.fieldSources && nutrient.fieldSources[fieldKey]) {
    return nutrient.fieldSources[fieldKey];
  }
  // Fallback to main source
  return nutrient.source || null;
};

export const getDRVFields = (nutrient: NutrientRequirement | any): DRVField[] => {
  const fields: DRVField[] = [
    { 
      key: 'rda', 
      label: 'RDA', 
      value: nutrient.rda,
      source: getFieldSource(nutrient, 'rda'),
    },
    { 
      key: 'ai', 
      label: 'AI', 
      value: nutrient.ai,
      source: getFieldSource(nutrient, 'ai'),
    },
    { 
      key: 'ear', 
      label: 'EAR', 
      value: nutrient.ear,
      source: getFieldSource(nutrient, 'ear'),
    },
    {
      key: 'ul',
      label: 'UL',
      value: nutrient.ul,
      // If grams-based UL is provided, treat UL as percentage
      unitOverride: nutrient.ulGrams != null ? '%' : undefined,
      valueFormatter: (v: number) =>
        nutrient.ulGrams != null ? `${v}%` : String(v),
      source: getFieldSource(nutrient, 'ul'),
    },
    // Grams-based fields (used by some macronutrients like total fats)
    {
      key: 'ulGrams',
      label: 'UL',
      value: nutrient.ulGrams,
      unitOverride: 'g',
      valueFormatter: (v: number) => String(v),
      source: getFieldSource(nutrient, 'ul'), // Same source as ul
    },
    { 
      key: 'lrni', 
      label: 'LRNI', 
      value: nutrient.lrni,
      source: getFieldSource(nutrient, 'lrni'),
    },
    {
      key: 'amdr',
      label: 'AMDR',
      value: nutrient.amdr,
      unitOverride: '%',
      valueFormatter: (v: number) => `${v}%`,
      source: getFieldSource(nutrient, 'amdr'),
    },
    {
      key: 'amdrGrams',
      label: 'AMDR',
      value: nutrient.amdrGrams,
      unitOverride: 'g',
      valueFormatter: (v: number) => String(v),
      source: getFieldSource(nutrient, 'amdr'),
    },
    {
      key: 'amdrLow',
      label: 'AMDR Low',
      value: nutrient.amdrLow,
      unitOverride: '%',
      valueFormatter: (v: number) => `${v}%`,
      source: getFieldSource(nutrient, 'amdrLow'),
    },
    {
      key: 'amdrHigh',
      label: 'AMDR High',
      value: nutrient.amdrHigh,
      unitOverride: '%',
      valueFormatter: (v: number) => `${v}%`,
      source: getFieldSource(nutrient, 'amdrHigh'),
    },
    {
      key: 'amdrLowGrams',
      label: 'AMDR Low',
      value: nutrient.amdrLowGrams,
      unitOverride: 'g',
      valueFormatter: (v: number) => String(v),
      source: getFieldSource(nutrient, 'amdrLow'), // Same source as amdrLow
    },
    {
      key: 'amdrHighGrams',
      label: 'AMDR High',
      value: nutrient.amdrHighGrams,
      unitOverride: 'g',
      valueFormatter: (v: number) => String(v),
      source: getFieldSource(nutrient, 'amdrHigh'), // Same source as amdrHigh
    },
  ].filter((f) => f.value !== null && f.value !== undefined);

  return fields;
};

// Helper function to format display name from key
export const getDisplayNameFromKey = (key: string) => {
  return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// Component to render a nutrient card
export const NutrientCard = ({ 
  nutrient, 
  nutrientId, 
  borderColor = 'border-cs-primary/20' 
}: { 
  nutrient: NutrientRequirement | any;
  nutrientId?: string;
  borderColor?: string;
}) => {
  const displayName = nutrient.displayName || (nutrientId ? getDisplayNameFromKey(nutrientId) : '');
  const unit = nutrient.unit || 'mg';
  const fields = getDRVFields(nutrient);

  return (
    <div className={`space-y-3 p-3 rounded-xl border-2 ${borderColor} bg-white shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden`}>
      <div className="flex items-center">
        <Label className="text-sm font-bold text-gray-800 block">
          {displayName}
        </Label>
      </div>
      {fields.length > 0 ? (
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 flex-wrap">
          {fields.map((field) => {
            const displayValue = typeof field.valueFormatter === 'function' 
              ? field.valueFormatter(field.value) 
              : field.value;
            const sourceName = formatSourceName(field.source);
            
            const valueElement = (
              <div className={`text-sm font-medium text-gray-900 ${sourceName ? 'cursor-help' : ''}`}>
                {displayValue}
              </div>
            );

            const content = (
              <>
                <Label className="text-xs sm:text-sm font-medium text-gray-700 whitespace-nowrap">
                  {field.label} ({field.unitOverride || unit})
                </Label>
                {valueElement}
              </>
            );

            return (
              <div key={field.key} className="space-y-1 flex-1 min-w-[110px] max-w-[140px] flex-shrink-0">
                {sourceName ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div>
                          {content}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-sm italic">{sourceName}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  content
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-sm text-gray-500">No values available</div>
      )}
    </div>
  );
};

// Component to render nutrients list (for fallback structure)
export const NutrientList = ({ 
  nutrients, 
  emptyMessage,
  borderColor = 'border-cs-primary/20'
}: { 
  nutrients: [string, any][];
  emptyMessage: string;
  borderColor?: string;
}) => {
  if (nutrients.length === 0) {
    return (
      <div className="text-center py-8 col-span-full">
        <p className="text-gray-500">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <>
      {nutrients.map(([nutrientType, values]: [string, any]) => (
        <NutrientCard 
          key={nutrientType}
          nutrient={values}
          nutrientId={nutrientType}
          borderColor={borderColor}
        />
      ))}
    </>
  );
};
